<template>
  <div :class="preview ? 'mt-0' : 'mt-3'">
    <div v-if="!preview" class="pt-4 mb-2">
      <router-link :to="`/datasets/${getID}`" class="page-btn mt-2">
        <i class="material-icons mr-1">keyboard_arrow_left</i>
        Back to dataset
      </router-link>
    </div>
    <div :class="preview ? 'mt-0' : 'mt-4'" v-if="!preview || displayedSimilarDatasets.length > 0">
      <h2 class="dsd-feature-title">{{ $t('message.datasetDetails.subnav.similarDatasets') }}</h2>
      <!-- <p v-if="!preview && rootCatalogueName" class="root-catalogue-name mb-3">
        <small>Catalogue: {{ rootCatalogueName }}</small>
      </p> -->
      <div class="mb-5 layout-fix">
        <p v-if="!preview && !similarDatasetsPresent && similarDatasetsFetched" class="mt-3 no-found-msg">
          {{ $t('message.similarDatasets.notFound') }}
        </p>
        <div class="mt-5" v-if="similarDatasetsPresent && similarDatasetsFetched && displayedSimilarDatasets.length > 0">
          <div class="row">
            <div v-for="(similarDataset, i) in displayedSimilarDatasets" :key="i" class="col-md-6 col-lg-4 mb-4">
              <app-link class="card-link" :to="similarDatasetLink(similarDataset.uri)">
                <div class="card h-100 shadow-sm">
                  <div class="accent"></div>
                  <div class="card-body d-flex flex-column">
                    <!-- Badge at top -->
                    <!-- <div class="mb-3">
                      <span v-if="showSimilarbadge(similarDataset.similarity, 'verySimilar')" 
                            class="badge badge-success">
                        {{ $t('message.similarDatasets.verySimilar') }}
                      </span>
                      <span v-else-if="showSimilarbadge(similarDataset.similarity, 'similar')" 
                            class="badge badge-info">
                        {{ $t('message.similarDatasets.similar') }}
                      </span>
                      <span v-else-if="showSimilarbadge(similarDataset.similarity, 'lessSimilar')" 
                            class="badge badge-warning">
                        {{ $t('message.similarDatasets.lessSimilar') }}
                      </span>
                      <span v-else class="badge badge-secondary">
                        {{ $t('message.similarDatasets.marginalSimilar') }}
                      </span>
                    </div> -->
                    <!-- Format on top -->
                    <div class="mb-3" v-if="has(similarDataset, 'distributionFormats')">
                      <PvDataInfoBoxFormats :formats="similarDataset.distributionFormats" />
                    </div>

                    <!-- Title -->
                    <h5 class="card-title">
                      {{ getTranslationFor(similarDataset.title, $route.query.locale, getLanguages) }}
                    </h5>

                    <!-- Catalgoue -->
                    <p v-if="getCatalogueName(similarDataset)" class="catalogue-name mb-2">
                       <small>{{ getCatalogueName(similarDataset) }}</small>
                    </p> 

                    <!-- Description -->
                    <p class="card-text flex-grow-1 truncate">
                      <small>
                        {{ getTranslationFor(similarDataset.description, $route.query.locale, getLanguages) }}
                      </small>
                    </p>
                  </div>
                </div>
              </app-link>
            </div>
          </div>

          <div
            v-if="preview && displayedSimilarDatasets.length > 0 && filteredSimilarDatasets.length > displayedSimilarDatasets.length"
            class="text-right mt-2"
          >
            <router-link :to="`/datasets/${getID}/similarDatasets?locale=${$route.query.locale}`" class="explore-btn">
              Explore similar datasets
              <i class="material-icons ml-2">arrow_forward</i>
            </router-link>
          </div>
        </div>

        <div
          class="mt-5"
          v-if="showSimilarDatasetsSkeleton"
        >
          <div class="row">
            <div
              v-for="placeholderIndex in skeletonCardCount"
              :key="`similar-skeleton-${placeholderIndex}`"
              class="col-md-6 col-lg-4 mb-4"
            >
              <div class="card h-100 shadow-sm skeleton-card" aria-hidden="true">
                <div class="card-body d-flex flex-column">
                  <div class="skeleton-line skeleton-line-format mb-3"></div>
                  <div class="skeleton-line skeleton-line-title mb-2"></div>
                  <div class="skeleton-line skeleton-line-catalogue mb-3"></div>
                  <div class="skeleton-line skeleton-line-description mb-2"></div>
                  <div class="skeleton-line skeleton-line-description short"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { has } from 'lodash-es';
import { helpers, AppLink } from "@piveau/piveau-hub-ui-modules"
const { getTranslationFor, appendCurrentLocaleToURL } = helpers;
import PvDataInfoBoxFormats from "../search/PvDataInfoBox/PvDataInfoBoxFormats.vue"

export default {
  name: 'DatasetDetailsSimilarDatasets',
  props: {
    preview: {
      type: Boolean,
      default: false,
    },
    limit: {
      type: Number,
      default: 0,
    },
  },
  components: {
    PvDataInfoBoxFormats,
    AppLink,
  },
  data() {
    return {
      similarDatasetsFetched: false,
      similarDatasetsPresent: false,
      breakpoints: this.$env.content.datasetDetails.similarDatasets.breakpoints,
      useSimilarDatasets: this.$env.content.datasetDetails.similarDatasets.useSimilarDatasets,
      similarDatasetsById: {},
      similarDatasetRequestsInFlight: {},
      unavailableSimilarDatasetIds: {},
      unavailableSimilarDatasetStorageKey: 'piveau.unavailableSimilarDatasetIds.v1',
      catalogueNamesByDatasetId: {},
      catalogueRequestsInFlight: {},
    };
  },
  computed: {
    // import store-getters
    ...mapGetters('datasetDetails', [
      'getID',
      'getCatalog',
      'getLanguages',
      'getSimilarDatasetsRequested',
      'getSimilarDatasets',
      'getTitle',
      'getDescription',
    ]),
    similarDatasets() {
      return this.getSimilarDatasets.map((dataset) => {
        const datasetId = this.getDatasetId(dataset);
        if (!datasetId || !this.similarDatasetsById[datasetId]) {
          return dataset;
        }

        return {
          ...dataset,
          ...this.similarDatasetsById[datasetId],
          id: dataset.id || this.similarDatasetsById[datasetId].id || datasetId,
          uri: dataset.uri || this.similarDatasetsById[datasetId].uri,
        };
      });
    },
    filteredSimilarDatasets() {
      return this.similarDatasets.filter(dataset => {
        const datasetId = this.getDatasetId(dataset);
        if (datasetId && this.unavailableSimilarDatasetIds[datasetId]) {
          return false;
        }

        const hasTitle = dataset.title && Object.keys(dataset.title).length > 0;
        const hasFormats = dataset.distributionFormats && dataset.distributionFormats.length > 0;
        const hasDescription = dataset.description && Object.keys(dataset.description).length > 0;
        return hasTitle && (hasFormats || hasDescription);
      });
    },
    displayedSimilarDatasets() {
      if (this.preview && this.limit > 0) {
        return this.filteredSimilarDatasets.slice(0, this.limit);
      }
      return this.filteredSimilarDatasets;
    },
    rootCatalogueName() {
      return this.extractCatalogueNameFromAnyPayload(this.getCatalog);
    },
    showSimilarDatasetsSkeleton() {
      return this.similarDatasetsFetched
        && this.similarDatasetsPresent
        && this.displayedSimilarDatasets.length === 0
        && Object.keys(this.similarDatasetRequestsInFlight).length > 0;
    },
    skeletonCardCount() {
      if (this.preview && this.limit > 0) {
        return this.limit;
      }
      return 3;
    },
  },
  methods: {
    // import store-actions
    ...mapActions('datasetDetails', [
      'loadDatasetDetails',
      'loadSimilarDatasets',
      'loadSimilarDatasetDetails',
    ]),
    has,
    appendCurrentLocaleToURL,
    getTranslationFor,
    canUseLocalStorage() {
      return typeof window !== 'undefined' && !!window.localStorage;
    },
    loadUnavailableSimilarDatasetIdsFromStorage() {
      if (!this.canUseLocalStorage()) {
        return;
      }

      try {
        const raw = window.localStorage.getItem(this.unavailableSimilarDatasetStorageKey);
        if (!raw) {
          return;
        }

        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
          return;
        }

        const activeIds = {};

        Object.entries(parsed).forEach(([datasetId, isUnavailable]) => {
          if (isUnavailable === undefined || isUnavailable === null || isUnavailable === false) {
            return;
          }
          activeIds[datasetId] = true;
        });

        if (Object.keys(activeIds).length > 0) {
          this.unavailableSimilarDatasetIds = {
            ...this.unavailableSimilarDatasetIds,
            ...activeIds,
          };
        }

        window.localStorage.setItem(this.unavailableSimilarDatasetStorageKey, JSON.stringify(activeIds));
      } catch (error) {
        console.warn('Failed to read unavailable similar datasets from localStorage', error);
      }
    },
    persistUnavailableSimilarDatasetId(datasetId) {
      if (!datasetId || !this.canUseLocalStorage()) {
        return;
      }

      try {
        const raw = window.localStorage.getItem(this.unavailableSimilarDatasetStorageKey);
        const parsed = raw ? JSON.parse(raw) : {};
        const nextCache = (parsed && typeof parsed === 'object') ? parsed : {};
        nextCache[datasetId] = true;
        window.localStorage.setItem(this.unavailableSimilarDatasetStorageKey, JSON.stringify(nextCache));
      } catch (error) {
        console.warn('Failed to persist unavailable similar dataset id', error);
      }
    },
    markUnavailableSimilarDatasetId(datasetId, persist = false) {
      if (!datasetId) {
        return;
      }

      this.unavailableSimilarDatasetIds = {
        ...this.unavailableSimilarDatasetIds,
        [datasetId]: true,
      };

      if (persist) {
        this.persistUnavailableSimilarDatasetId(datasetId);
      }
    },
    /**
     * Update all similar datasets by adding title and description
     */
    updateSimilarDatasets() {
      this.similarDatasets.forEach((dataset) => {
        this.getDatasetDetails(dataset);
      });
      this.fetchMissingCatalogueNames();
    },
    /**
     * Get dataset details by id
     */
    getDatasetDetails(similarDataset) {
      const datasetId = this.getDatasetId(similarDataset);
      if (!datasetId) {
        return;
      }

      if (this.unavailableSimilarDatasetIds[datasetId]) {
        return;
      }

      if (this.similarDatasetsById[datasetId] || this.similarDatasetRequestsInFlight[datasetId]) {
        return;
      }

      this.similarDatasetRequestsInFlight = {
        ...this.similarDatasetRequestsInFlight,
        [datasetId]: true,
      };

      this.loadSimilarDatasetDetails(datasetId)
        .then((datasetDetails) => {
          const normalizedDataset = this.extractSimilarDatasetFromResponse(datasetDetails);
          if (normalizedDataset && typeof normalizedDataset === 'object') {
            this.similarDatasetsById = {
              ...this.similarDatasetsById,
              [datasetId]: {
                ...normalizedDataset,
                id: normalizedDataset.id || datasetId,
                uri: normalizedDataset.uri || similarDataset?.uri,
              },
            };
          }

          const catalogueName = this.extractCatalogueNameFromAnyPayload(datasetDetails);
          if (datasetId && catalogueName) {
            this.catalogueNamesByDatasetId = {
              ...this.catalogueNamesByDatasetId,
              [datasetId]: catalogueName,
            };
          }
        })
        .catch((error) => {
          const status = error?.response?.status;
          if (status === 404) {
            this.markUnavailableSimilarDatasetId(datasetId, true);
          }
          if (status === 401 || status === 403) {
            this.markUnavailableSimilarDatasetId(datasetId, false);
          }
          if (status !== 404 && status !== 401 && status !== 403) {
            console.warn("Failed to load similar dataset details", error);
          }
        })
        .finally(() => {
          const remainingRequests = {
            ...this.similarDatasetRequestsInFlight,
          };
          delete remainingRequests[datasetId];
          this.similarDatasetRequestsInFlight = remainingRequests;
        });
    },
    extractSimilarDatasetFromResponse(responseData) {
      return responseData?.result || responseData?.data || responseData;
    },
    showSimilarbadge(similarity, similarType) {
      return similarity >= this.breakpoints[similarType].start && similarity < this.breakpoints[similarType].end;
    },
    similarDatasetLink(url) {
      const id = this.extractDatasetIdentifier(url);
      if (!id) {
        return '#';
      }
      if (this.preview) {
        return id + `?locale=${this.$route.query.locale}`;
      }
      return "../" + id + `?locale=${this.$route.query.locale}`;
    },
    extractDatasetIdentifier(value) {
      if (!value || typeof value !== 'string') {
        return undefined;
      }

      let normalizedValue = value.trim();
      if (!normalizedValue) {
        return undefined;
      }

      if (normalizedValue.includes('://')) {
        try {
          normalizedValue = new URL(normalizedValue).pathname;
        } catch (error) {
          // Keep the original value if URL parsing fails.
        }
      }

      const withoutQuery = normalizedValue.split('?')[0].split('#')[0];
      const segments = withoutQuery.split('/').filter(Boolean);
      if (segments.length === 0) {
        return undefined;
      }

      return segments[segments.length - 1];
    },
    getDatasetId(similarDataset) {
      if (similarDataset?.id) {
        const extractedFromId = this.extractDatasetIdentifier(similarDataset.id);
        if (extractedFromId) {
          return extractedFromId;
        }
      }
      if (similarDataset?.uri) {
        return this.extractDatasetIdentifier(similarDataset.uri);
      }
      return undefined;
    },
    getPreferredString(value) {
      if (!value) {
        return "";
      }
      if (Array.isArray(value)) {
        return value
          .map((item) => this.getPreferredString(item))
          .find((item) => !!item) || "";
      }
      if (typeof value === 'string') {
        return value;
      }
      if (typeof value === 'object') {
        return this.getTranslationFor(value, this.$route.query.locale, this.getLanguages)
          || Object.values(value).find(v => typeof v === 'string')
          || "";
      }
      return "";
    },
    extractCatalogueNameFromAnyPayload(payload) {
      if (!payload) {
        return "";
      }

      const directCandidates = [
        payload?.catalogue,
        payload?.catalog,
        payload?.isPartOf,
        payload?.is_part_of,
        payload?.catalogRecord,
      ];

      for (const candidate of directCandidates) {
        const directName = this.getPreferredString(
          candidate?.title || candidate?.name || candidate?.prefLabel || candidate?.label || candidate
        );
        if (directName) {
          return directName;
        }
      }

      const topLevelName = this.getPreferredString(
        payload?.catalogueTitle || payload?.catalogTitle || payload?.catalogName || payload?.catalogueName
      );
      if (topLevelName) {
        return topLevelName;
      }

      return this.findCatalogueNameDeep(payload);
    },
    findCatalogueNameDeep(payload, depth = 0) {
      if (!payload || depth > 6) {
        return "";
      }

      if (Array.isArray(payload)) {
        for (const item of payload) {
          const fromItem = this.findCatalogueNameDeep(item, depth + 1);
          if (fromItem) {
            return fromItem;
          }
        }
        return "";
      }

      if (typeof payload !== 'object') {
        return "";
      }

      for (const [key, value] of Object.entries(payload)) {
        if (/(catalog|catalogue|is_part_of|isPartOf)/i.test(key)) {
          const name = this.getPreferredString(
            value?.title || value?.name || value?.prefLabel || value?.label || value
          );
          if (name) {
            return name;
          }
        }
      }

      for (const value of Object.values(payload)) {
        const nestedName = this.findCatalogueNameDeep(value, depth + 1);
        if (nestedName) {
          return nestedName;
        }
      }

      return "";
    },
    extractCatalogueNameFromDataset(dataset) {
      return this.extractCatalogueNameFromAnyPayload(dataset);
    },
    extractCatalogueNameFromResponse(responseData) {
      return this.extractCatalogueNameFromAnyPayload(responseData?.result || responseData?.data || responseData);
    },
    getCatalogueName(similarDataset) {
      const inlineCatalogueName = this.extractCatalogueNameFromDataset(similarDataset);
      if (inlineCatalogueName) {
        return inlineCatalogueName;
      }
      const datasetId = this.getDatasetId(similarDataset);
      return (datasetId && this.catalogueNamesByDatasetId[datasetId]) || "";
    },
    async fetchCatalogueNameForDataset(similarDataset) {
      const datasetId = this.getDatasetId(similarDataset);
      if (!datasetId) {
        return;
      }

      if (this.catalogueNamesByDatasetId[datasetId] !== undefined || this.catalogueRequestsInFlight[datasetId]) {
        return;
      }

      const inlineCatalogueName = this.extractCatalogueNameFromDataset(similarDataset);
      if (inlineCatalogueName) {
        this.catalogueNamesByDatasetId = {
          ...this.catalogueNamesByDatasetId,
          [datasetId]: inlineCatalogueName,
        };
        return;
      }

      this.catalogueNamesByDatasetId = {
        ...this.catalogueNamesByDatasetId,
        [datasetId]: "",
      };
      return;
    },
    fetchMissingCatalogueNames() {
      this.similarDatasets.forEach((dataset) => {
        this.fetchCatalogueNameForDataset(dataset);
      });
    }
  },
  created() {
    this.$nextTick(() => {
      if (this.useSimilarDatasets) {
        this.loadUnavailableSimilarDatasetIdsFromStorage();
        // Duplicated API call, execute only if data not already loaded
        if (this.$route.params.ds_id !== this.getID) {
          this.$Progress.start();
          this.loadDatasetDetails(this.$route.params.ds_id)
            .then(() => {
              this.loadSimilarDatasets(this.$route.params.ds_id)
                .then((response) => {
                  this.$nextTick(() => {
                    this.updateSimilarDatasets();
                    this.similarDatasetsFetched = true;
                    this.similarDatasetsPresent = response.length > 0;
                  });
                  this.$Progress.finish();
                })
                .catch(() => {
                  this.similarDatasetsFetched = true;
                  this.$Progress.fail();
                });
            })
            .catch(() => {
              this.$Progress.fail();
              this.$router.replace({
                name: 'NotFound',
                query: { locale: this.$route.query.locale, dataset: this.$route.params.ds_id },
              });
            });
        } else {
          // Duplicated API call, execute only if data not already loaded
          if (this.$route.params.ds_id !== this.getSimilarDatasetsRequested) {
            this.loadSimilarDatasets(this.$route.params.ds_id)
              .then((response) => {
                this.$nextTick(() => {
                  this.updateSimilarDatasets();
                  this.similarDatasetsFetched = true;
                  this.similarDatasetsPresent = response.length > 0;
                });
                this.$Progress.finish();
              })
              .catch(() => {
                this.similarDatasetsFetched = true;
                this.$Progress.fail();
              });
          } else {
            this.updateSimilarDatasets();
            this.similarDatasetsFetched = true;
            this.similarDatasetsPresent = this.getSimilarDatasets.length > 0;
          }
        }
      }
    });
  },
  setup() {
    //   metaInfo.useDatasetDetailsSimilarDatasetsHead();
  }
}

</script>

<style scoped>

.truncate {
  line-height: 1.4;                
  max-height: calc(1.4em * 5);     
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  line-clamp: 5;
  -webkit-line-clamp: 5;
}
.layout-fix {
  margin-left: -12px;
}
.card {
  border-radius: 4px;
  border:none !important;
  position: relative !important;
  box-shadow: 0px -4px 4px 0px #09318E0A !important;
  box-shadow: 0px 4px 5px 0px #09318E0A !important;
  box-shadow: 0px 0px 10px 0px #09318E0A !important;
  box-shadow: 0px 2px 4px 0px #09318E14 !important;
}
.card-link,
.card-link:hover,
.card-link:focus,
.card-link:active {
  text-decoration: none !important;
}

.card-link {
  display: block;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  &:hover .card {
    transform: translateY(-4px);
    box-shadow: 0px -4px 8px 0px #09318E14,
      0px 8px 10px 0px #09318E14,
      0px 0px 20px 0px #09318E14,
      0px 4px 8px 0px #09318E1F !important;
  }

  &:active .card {
    transform: translateY(-2px);
  }

  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none !important;
    outline: none;
  }
}

.card-text {
  color: #000;
}

.catalogue-name {
  color: #515560;
}

.root-catalogue-name {
  color: #515560;
}

.card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 20px;
  right: 0;
  height: 4px;
  width: 32px;
  border-bottom-right-radius: 2px;
  border-bottom-left-radius: 2px;
  background-color: #FFCC00;
}

.card-body :hover {
  text-decoration: none !important;
}

.no-found-msg {
  background-color: #f3f3f3;
  width: 300px;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
}

.page-btn {
  height: 48px;
  border-radius: 4px;
  padding: 16px;
  width: 200px;
  display: flex;
  align-items: center;
  text-decoration: none;
  border: 2px solid #0E47CB;
  justify-content: center;

  &:hover {
    background-color: #3E6CD5;
    color: #fff;
    border: 2px solid #3E6CD5;
  }

}

.explore-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  border-radius: 4px;
  padding: 0 32px;
  text-decoration: none;
  color: #0E47CB;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    text-decoration: none;
    color: #0E47CB;
    transform: translateY(-2px);
  }
}

.skeleton-card {
  pointer-events: none;
}

.skeleton-line {
  border-radius: 4px;
  background: linear-gradient(90deg, #eceff4 25%, #f7f9fc 37%, #eceff4 63%);
  background-size: 400% 100%;
  animation: skeleton-loading 1.2s ease-in-out infinite;
}

.skeleton-line-format {
  height: 18px;
  width: 42%;
}

.skeleton-line-title {
  height: 18px;
  width: 90%;
}

.skeleton-line-catalogue {
  height: 14px;
  width: 55%;
}

.skeleton-line-description {
  height: 12px;
  width: 100%;
}

.skeleton-line-description.short {
  width: 74%;
}

@keyframes skeleton-loading {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: 0 0;
  }
}
</style>
